class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      if (this.input.form) {
        this.input.form.addEventListener('reset', this.onFormReset.bind(this));
        this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      }
      this.input.addEventListener(
        'input',
        theme.utils.debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  normalizeSearchText(value) {
    return value
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[’'`´\-–—_,.;:/()[\]{}+]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getSearchWords(value) {
    return this.normalizeSearchText(value)
      .split(' ')
      .filter((word) => word.length > 1);
  }

  getProductMatchScore(queryWords, productTitle) {
    const normalizedTitle = this.normalizeSearchText(productTitle);
    if (!queryWords.length || !normalizedTitle) return 0;

    const matchedWords = queryWords.filter((word) => normalizedTitle.includes(word)).length;
    return matchedWords / queryWords.length;
  }

  async findExactProductForLongQuery(query) {
    const queryWords = this.getSearchWords(query);
    if (query.length < 20 || queryWords.length < 4) return null;

    const requestUrl = new URL('/search/suggest.json', window.location.origin);
    requestUrl.searchParams.set('q', query);
    requestUrl.searchParams.set('resources[type]', 'product');
    requestUrl.searchParams.set('resources[limit]', '10');

    const response = await fetch(requestUrl.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const payload = await response.json();
    const products = payload?.resources?.results?.products || [];
    if (!products.length) return null;

    const normalizedQuery = this.normalizeSearchText(query);
    const exactMatch = products.find((product) => this.normalizeSearchText(product.title) === normalizedQuery);
    if (exactMatch?.url) return exactMatch;

    const scoredProducts = products
      .map((product) => ({
        product,
        score: this.getProductMatchScore(queryWords, product.title),
      }))
      .filter((entry) => entry.score >= 0.85)
      .sort((a, b) => b.score - a.score);

    if (scoredProducts.length === 1 || scoredProducts[0]?.score > scoredProducts[1]?.score) {
      return scoredProducts[0].product;
    }

    return null;
  }

  async onFormSubmit(event) {
    if (event.defaultPrevented) return;

    const query = this.input?.value?.trim() || '';
    if (!query || this.input.form?.dataset.exactSearchSubmitting === 'true') return;
    if (window.epSkuSearch?.isCode(query)) {
      event.preventDefault();
      window.epSkuSearch.submit(query);
      return;
    }
    const queryWords = this.getSearchWords(query);

    if (queryWords.length === 1 && queryWords[0].length >= 3 && !query.includes('*') && !/^[a-z0-9]{2,4}\d{4}(?:-[a-z0-9.]+)*$/i.test(query)) {
      event.preventDefault();
      this.input.form.dataset.exactSearchSubmitting = 'true';
      this.input.value = `${query}*`;
      this.input.form.submit();
      return;
    }

    if (query.length < 20 || queryWords.length < 4) return;

    event.preventDefault();

    try {
      const product = await this.findExactProductForLongQuery(query);
      if (product?.url) {
        window.location.href = product.url;
        return;
      }
    } catch (error) {
      // Arama kesilmesin; oneriler alinamazsa normal Shopify aramasi devam eder.
    }

    this.input.form.dataset.exactSearchSubmitting = 'true';
    this.input.form.submit();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}
if (!customElements.get('search-form')) customElements.define('search-form', SearchForm);

(function () {
  'use strict';

  function normalizeSearchText(value) {
    return value
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[’'`´\-–—_,.;:/()[\]{}+]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getSearchWords(value) {
    return normalizeSearchText(value)
      .split(' ')
      .filter((word) => word.length > 1);
  }

  function getProductMatchScore(queryWords, productTitle) {
    const normalizedTitle = normalizeSearchText(productTitle);
    if (!queryWords.length || !normalizedTitle) return 0;

    const matchedWords = queryWords.filter((word) => normalizedTitle.includes(word)).length;
    return matchedWords / queryWords.length;
  }

  function ensureSearchHiddenInputs(form) {
    if (!form.querySelector('input[name="type"]')) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'type';
      input.value = 'product';
      form.appendChild(input);
    }

    if (!form.querySelector('input[name="options[prefix]"]')) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'options[prefix]';
      input.value = 'last';
      form.appendChild(input);
    }
  }

  function isSearchForm(form) {
    if (!form || form.method.toLowerCase() !== 'get') return false;
    const action = form.getAttribute('action') || '';
    return action.includes('/search') || action === '' || action === window.routes?.search_url;
  }

  async function findExactProductForLongQuery(query) {
    const queryWords = getSearchWords(query);
    if (query.length < 20 || queryWords.length < 4) return null;

    const requestUrl = new URL('/search/suggest.json', window.location.origin);
    requestUrl.searchParams.set('q', query);
    requestUrl.searchParams.set('resources[type]', 'product');
    requestUrl.searchParams.set('resources[limit]', '10');

    const response = await fetch(requestUrl.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const payload = await response.json();
    const products = payload?.resources?.results?.products || [];
    if (!products.length) return null;

    const normalizedQuery = normalizeSearchText(query);
    const exactMatch = products.find((product) => normalizeSearchText(product.title) === normalizedQuery);
    if (exactMatch?.url) return exactMatch;

    const scoredProducts = products
      .map((product) => ({
        product,
        score: getProductMatchScore(queryWords, product.title),
      }))
      .filter((entry) => entry.score >= 0.85)
      .sort((a, b) => b.score - a.score);

    if (scoredProducts.length === 1 || scoredProducts[0]?.score > scoredProducts[1]?.score) {
      return scoredProducts[0].product;
    }

    return null;
  }

  document.addEventListener(
    'submit',
    async function (event) {
      const form = event.target;
      if (!isSearchForm(form) || form.dataset.exactSearchSubmitting === 'true') return;

      const input = form.querySelector('input[type="search"][name="q"], input[name="q"]');
      const query = input?.value?.trim() || '';
      if (!query) return;
      if (window.epSkuSearch?.isCode(query)) {
        event.preventDefault();
        window.epSkuSearch.submit(query);
        return;
      }

      const queryWords = getSearchWords(query);
      ensureSearchHiddenInputs(form);

      if (queryWords.length === 1 && queryWords[0].length >= 3 && !query.includes('*') && !/^[a-z0-9]{2,4}\d{4}(?:-[a-z0-9.]+)*$/i.test(query)) {
        event.preventDefault();
        form.dataset.exactSearchSubmitting = 'true';
        input.value = `${query}*`;
        form.submit();
        return;
      }

      if (query.length < 20 || queryWords.length < 4) return;

      event.preventDefault();

      try {
        const product = await findExactProductForLongQuery(query);
        if (product?.url) {
          window.location.href = product.url;
          return;
        }
      } catch (error) {
        // Normal aramayi bozma.
      }

      form.dataset.exactSearchSubmitting = 'true';
      form.submit();
    },
    true
  );

  function redirectEmptyShortSearch() {
    if (window.location.pathname !== '/search') return;

    const params = new URLSearchParams(window.location.search);
    const query = params.get('q')?.trim() || '';
    if (window.epSkuSearch?.isCode(query)) return;
    const queryWords = getSearchWords(query);
    if (query.includes('*') || queryWords.length !== 1 || queryWords[0].length < 3 || /^[a-z0-9]{2,4}\d{4}(?:-[a-z0-9.]+)*$/i.test(query)) return;
    if (document.querySelector('.product-grid__item')) return;

    params.set('q', `${query}*`);
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    window.location.replace(`${window.location.pathname}?${params.toString()}`);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', redirectEmptyShortSearch);
  } else {
    redirectEmptyShortSearch();
  }
})();


(function () {
  'use strict';
  if (window.epSkuSearch) return;
  let catalogPromise;
  const root = window.Shopify?.routes?.root || '/';
  const normalize = (value) => String(value || '').normalize('NFKC').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const isCode = (value) => /^(?:[A-Z]{2,3}|2G)\d{4}(?:[-\s]?[A-Z0-9.]+)*\*?$/i.test(String(value || '').trim());
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  async function loadCatalog() {
    if (!catalogPromise) {
      catalogPromise = (async () => {
        const products = [];
        for (let page = 1; ; page += 1) {
          const url = new URL(`${root}products.json`, window.location.origin);
          url.searchParams.set('limit', '250');
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
          const payload = await response.json();
          if (!Array.isArray(payload.products)) throw new Error('Invalid catalog response');
          products.push(...payload.products);
          if (payload.products.length < 250) return products;
        }
      })().catch((error) => {
        catalogPromise = undefined;
        throw error;
      });
    }
    return catalogPromise;
  }

  function matchProducts(products, query) {
    const key = normalize(query);
    if (!key || !isCode(query)) return [];
    return products.flatMap((product) => {
      const variants = (product.variants || []).filter((variant) => normalize(variant.sku).startsWith(key));
      if (!variants.length) return [];
      const variant = variants.find((item) => normalize(item.sku) === key) || variants.find((item) => item.available) || variants[0];
      const url = new URL(`${root}products/${encodeURIComponent(product.handle)}`, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      return [{ product, variant, url: url.pathname + url.search, exact: normalize(variant.sku) === key }];
    }).sort((a, b) => Number(b.exact) - Number(a.exact) || a.product.title.localeCompare(b.product.title, 'tr'));
  }

  function styles() {
    if (document.getElementById('ep-sku-search-style')) return;
    const style = document.createElement('style');
    style.id = 'ep-sku-search-style';
    style.textContent = '.ep-sku-results{display:grid;gap:16px;grid-template-columns:repeat(auto-fit,minmax(min(100%,240px),1fr));padding:0;list-style:none;margin:24px 0}.ep-sku-card{display:flex;gap:16px;align-items:center;color:inherit;text-decoration:none;padding:16px;border:1px solid #e4d7f2;border-radius:12px;background:#fff;min-height:110px}.ep-sku-card:hover,.ep-sku-card:focus-visible{border-color:#7430b5;outline:2px solid #7430b5;outline-offset:2px}.ep-sku-card img{width:80px;height:80px;object-fit:contain;flex-shrink:0}.ep-sku-card strong{display:block;font-size:15px;line-height:1.5}.ep-sku-card small{display:block;margin-top:6px;font-size:13px;color:#614176;overflow-wrap:anywhere}.ep-sku-card span.ep-sku-cta{display:block;margin-top:8px;font-size:13px;color:#7430b5}.ep-sku-predictive{list-style:none;padding:8px;margin:0}.ep-sku-predictive .ep-sku-card{border:0;padding:12px}.ep-sku-predictive .ep-sku-card img{width:56px;height:56px}.ep-sku-message{padding:16px;line-height:1.6}.ep-sku-retry{cursor:pointer;text-decoration:underline;background:none;border:0;color:#7430b5;padding:8px}';
    document.head.appendChild(style);
  }

  function resultCard(result, index, predictive) {
    const { product, variant, url } = result;
    const source = variant.featured_image?.src || product.images?.[0]?.src;
    let image = '';
    if (source) {
      const imageUrl = new URL(source, window.location.origin);
      imageUrl.searchParams.set('width', predictive ? '112' : '160');
      image = `<img src="${escape(imageUrl.href)}" alt="${escape(product.title)}" width="80" height="80" loading="lazy">`;
    }
    return `<li${predictive ? ` id="ep-sku-option-${index}" role="option" aria-selected="false"` : ''}><a class="ep-sku-card" href="${escape(url)}">${image}<span><strong>${escape(product.title)}</strong><small>Ürün kodu: ${escape(variant.sku)}</small>${predictive ? '' : '<span class="ep-sku-cta">Ürünü incele →</span>'}</span></a></li>`;
  }

  function submit(query) {
    const url = new URL(window.routes?.search_url || `${root}search`, window.location.origin);
    url.searchParams.set('q', query.trim().replace(/\*$/, ''));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    window.location.assign(url.href);
  }

  async function renderPredictive(instance, query) {
    styles();
    instance.abortController?.abort();
    instance.abortController = new AbortController();
    const token = (instance.epSkuRequest || 0) + 1;
    instance.epSkuRequest = token;
    instance.setLiveRegionLoadingState();
    try {
      const results = matchProducts(await loadCatalog(), query).slice(0, 10);
      if (instance.epSkuRequest !== token || instance.getQuery() !== query) return;
      const markup = `<div data-predictive-search-live-region-count-value class="visually-hidden">${results.length} ürün bulundu</div>${results.length ? `<ul class="ep-sku-predictive" role="listbox" aria-label="Ürün kodu arama sonuçları">${results.map((result, index) => resultCard(result, index, true)).join('')}</ul>` : '<p class="ep-sku-message">Bu ürün koduyla eşleşen ürün bulunamadı.</p>'}`;
      instance.renderSearchResults(markup);
    } catch (error) {
      if (instance.epSkuRequest !== token || instance.getQuery() !== query) return;
      instance.renderSearchResults('<div data-predictive-search-live-region-count-value class="visually-hidden">Arama yüklenemedi</div><p class="ep-sku-message">Ürün kodları şu anda yüklenemedi. Tekrar deneyin.</p>');
    }
  }

  async function renderResultsPage() {
    if (!window.location.pathname.replace(/\/$/, '').endsWith('/search')) return;
    const query = new URLSearchParams(window.location.search).get('q')?.trim() || '';
    if (!isCode(query)) return;
    const section = document.querySelector('.template-search > .section');
    const header = section?.querySelector('.template-search__header');
    if (!section || !header) return;
    styles();
    section.closest('.template-search')?.classList.remove('template-search--empty');
    let wrapper = section.querySelector('.collection-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      section.appendChild(wrapper);
    }
    wrapper.className = 'ep-sku-search-content page-width';
    wrapper.setAttribute('aria-busy', 'true');
    header.querySelectorAll('p[role="status"]').forEach((element) => element.remove());
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = 'Ürün kodu aranıyor…';
    header.appendChild(status);
    wrapper.innerHTML = '';
    try {
      const results = matchProducts(await loadCatalog(), query);
      status.textContent = `“${query}” için ${results.length} ürün bulundu.`;
      wrapper.innerHTML = results.length ? `<ul class="ep-sku-results">${results.map((result, index) => resultCard(result, index, false)).join('')}</ul>` : '<p class="ep-sku-message">Bu ürün koduyla eşleşen ürün bulunamadı.</p>';
    } catch (error) {
      status.textContent = 'Ürün kodu araması yüklenemedi.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'ep-sku-retry';
      retry.textContent = 'Tekrar dene';
      retry.addEventListener('click', () => window.location.reload());
      wrapper.replaceChildren(retry);
    } finally {
      wrapper.removeAttribute('aria-busy');
    }
  }

  window.epSkuSearch = { isCode, normalize, matchProducts, loadCatalog, renderPredictive, submit };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderResultsPage, { once: true });
  else renderResultsPage();
})();
